import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { Home, ArrowLeft, RefreshCw, AlertTriangle, ShieldAlert, SearchX } from 'lucide-react';

interface ErrorPageProps {
  status?: number;
  title?: string;
  message?: string;
  /** Hide sidebar layout — for public/unauthenticated pages */
  standalone?: boolean;
  /** Show a retry button that calls this */
  onRetry?: () => void;
}

const errorIcons: Record<number, typeof AlertTriangle> = {
  403: ShieldAlert,
  404: SearchX,
  500: AlertTriangle,
};

const defaults: Record<number, { title: string; message: string }> = {
  403: {
    title: 'Access Denied',
    message: "You don't have permission to access this page. If you believe this is a mistake, please contact your administrator.",
  },
  404: {
    title: 'Page Not Found',
    message: 'The page you are looking for does not exist or has been moved. Check the URL or navigate to a different page.',
  },
  500: {
    title: 'Internal Server Error',
    message: 'Something went wrong on our end. Please try again later or contact support if the issue persists.',
  },
};

export function ErrorPage({
  status = 404,
  title,
  message,
  standalone = false,
  onRetry,
}: ErrorPageProps) {
  const navigate = useNavigate();
  const config = defaults[status] || defaults[500];
  const Icon = errorIcons[status] || errorIcons[500];
  const displayTitle = title || config.title;
  const displayMessage = message || config.message;

  useDocumentTitle(displayTitle);

  const content = (
    <div className="flex flex-col items-center justify-center text-center px-4 py-8">
      {/* Error icon with status badge */}
      <div className="relative mb-7">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[var(--bg-secondary)] to-[var(--bg-primary)] border border-[var(--border-color)] flex items-center justify-center shadow-lg shadow-[var(--border-color)]/10">
          <Icon size={36} className={
            status === 403 ? 'text-orange-400' :
            status === 404 ? 'text-blue-400' :
            'text-red-400'
          } />
        </div>
        <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-[var(--bg-primary)] border border-[var(--border-color)] text-[11px] font-bold text-[var(--text-secondary)] leading-none">
          {status}
        </div>
      </div>

      {/* Title */}
      <h1 className="text-2xl md:text-3xl font-bold mb-3">{displayTitle}</h1>

      {/* Message */}
      <p className="text-sm text-[var(--text-secondary)] max-w-md mb-8 leading-relaxed">
        {displayMessage}
      </p>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="gap-2"
        >
          <ArrowLeft size={15} />
          Go Back
        </Button>

        <Button asChild className="gap-2">
          <Link to="/">
            <Home size={15} />
            {standalone ? 'Sign In' : 'Dashboard'}
          </Link>
        </Button>

        {onRetry && (
          <Button variant="outline" onClick={onRetry} className="gap-2">
            <RefreshCw size={15} />
            Try Again
          </Button>
        )}
      </div>

      {/* Help text */}
      <p className="mt-8 text-xs text-[var(--text-secondary)]">
        Need help?{' '}
        <a
          href={`mailto:support@dyndev.co.uk?subject=${encodeURIComponent(`Error ${status} - ${displayTitle}`)}`}
          className="text-dyndev-green hover:underline"
        >
          Contact Support
        </a>
      </p>
    </div>
  );

  if (standalone) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <div className="bg-noise opacity-[0.02] pointer-events-none fixed inset-0" />
        <div className="w-full max-w-lg relative z-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="text-4xl font-bold tracking-tight">
                Dyn<span className="text-dyndev-green">Dev</span>
              </h1>
            </Link>
          </div>
          {content}
          <p className="text-center mt-8 text-xs text-[var(--text-secondary)]">
            &copy; {new Date().getFullYear()} DynDev. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  return content;
}
